'use client';

import * as React from 'react';

export interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Optional label next to the switch */
  label?: React.ReactNode;
  /** Accessible name for the input when no visible label is rendered */
  ariaLabel?: string;
  className?: string;
}

/**
 * A simple Tailwind‐styled switch control.
 *
 * <Switch
 *   checked={isOn}
 *   onCheckedChange={setIsOn}
 *   label="Enable notifications"
 * />
 */
export function Switch({
  checked,
  onCheckedChange,
  disabled = false,
  label,
  ariaLabel,
  className = '',
}: SwitchProps) {
  return (
    <label className={`inline-flex items-center space-x-2 cursor-pointer select-none ${className}`}>
      <div className="relative">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onCheckedChange(e.target.checked)}
          className="sr-only peer"
          aria-label={ariaLabel ?? (label ? undefined : 'Toggle')}
        />
        <div
          className={`
            w-10 h-5 rounded-full bg-gray-300
            peer-checked:bg-[hsl(var(--primary))]
            peer-disabled:opacity-50
            transition-colors
          `}
          aria-hidden="true"
        />
        <div
          className={`
            absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white
            transform peer-checked:translate-x-5
            peer-disabled:opacity-50
            transition-transform
          `}
          aria-hidden="true"
        />
      </div>
      {label && <span className="text-sm font-medium">{label}</span>}
    </label>
  );
}
